// The shapes of GitHub's answers that are decoded where they are fetched
// (see api/decode.ts for why and how), and a reader for each. Each shape
// mirrors its query in api/queries.ts field by field; the result types
// there describe what these readers return.
//
// Decoded: the pull request (PR_QUERY) and its further pages of threads,
// thread comments and timeline; its checks (PR_CHECKS); its deployments
// (PR_DEPLOYMENTS); its stack (PR_STACK_QUERY). The dashboard's, the related issues', the repository
// tree's and the reference details' answers are still read as typed: a
// follow-up, query by query, each with its shape here.
import type { DeploymentCommit } from '../logic/previews';
import { decode, flag, key, list, MISSING, nodes, nul, num, obj, opt, str, union, type Shape } from './decode';
import type { BackwardPageInfo, Labels, PageInfo, PrQueryResult, PrStackResult, PullRequestData, ThreadComment, TimelineItem } from './queries';

const pageInfo = obj({ hasNextPage: flag(), endCursor: nul(str()) });
const backwardPageInfo = obj({ hasPreviousPage: flag(), startCursor: nul(str()) });
/** `{ __typename login avatarUrl }`: nobody when the account is gone or withheld. */
const actor = nul(obj({ login: key }));
const reviewer = nul(
  union('__typename', {
    User: obj({ login: key }),
    Bot: obj({ login: key }),
    Mannequin: obj({ login: key }),
    Team: obj({ slug: key, name: str() }),
  }),
);
const commitRef = obj({ oid: key, abbreviatedOid: str() });
const count = obj({ totalCount: num() });
const event = obj({ id: key, createdAt: str(), actor });
const forcePush = obj({ id: key, createdAt: str(), actor, beforeCommit: nul(commitRef), afterCommit: nul(commitRef) });
const reviewRequest = obj({ id: key, createdAt: str(), actor, requestedReviewer: reviewer });

const timelineItem = union('__typename', {
  PullRequestCommit: obj({
    id: key,
    commit: obj({
      oid: key,
      abbreviatedOid: str(),
      messageHeadline: str(),
      committedDate: str(),
      parents: opt(count),
      author: nul(obj({ name: nul(str()), user: nul(obj({ login: key })) })),
    }),
  }),
  HeadRefForcePushedEvent: forcePush,
  BaseRefForcePushedEvent: forcePush,
  ReviewRequestedEvent: reviewRequest,
  ReviewRequestRemovedEvent: reviewRequest,
  PullRequestReview: obj({
    id: key,
    createdAt: str(),
    submittedAt: nul(str()),
    state: str('COMMENTED'),
    body: str(),
    bodyHTML: str(),
    url: str(),
    author: actor,
    comments: count,
    reviewCommit: opt(nul(obj({ oid: key }))),
  }),
  IssueComment: obj({
    id: key,
    databaseId: num(),
    createdAt: str(),
    lastEditedAt: opt(nul(str())),
    authorAssociation: opt(str('NONE')),
    body: str(),
    bodyHTML: str(),
    url: str(),
    viewerCanUpdate: flag(),
    author: actor,
  }),
  ReadyForReviewEvent: event,
  ConvertToDraftEvent: event,
  ClosedEvent: event,
  ReopenedEvent: event,
  MergedEvent: obj({ id: key, createdAt: str(), actor, mergeCommit: nul(commitRef) }),
});
const timelineItems = nodes(timelineItem, { totalCount: opt(num()), pageInfo: backwardPageInfo });

const threadComment = obj({
  id: key,
  databaseId: num(),
  body: str(),
  bodyHTML: str(),
  createdAt: str(),
  url: str(),
  author: actor,
  pullRequestReview: nul(obj({ id: str(), state: str() })),
  originalCommit: nul(obj({ oid: key })),
  diffHunk: opt(str()),
});
const threadComments = nodes(threadComment, { totalCount: num(), pageInfo: opt(pageInfo) });
const thread = obj({
  id: key,
  isResolved: flag(),
  isOutdated: flag(),
  path: str(),
  line: nul(num()),
  originalLine: nul(num()),
  startLine: nul(num()),
  originalStartLine: nul(num()),
  diffSide: str('RIGHT'),
  startDiffSide: nul(str('RIGHT')),
  subjectType: str('LINE'),
  viewerCanResolve: flag(),
  viewerCanUnresolve: flag(),
  viewerCanReply: flag(),
  resolvedBy: nul(obj({ login: key })),
  comments: threadComments,
});
const reviewThreads = nodes(thread, { totalCount: opt(num()), pageInfo });

const checkContext = union('__typename', {
  CheckRun: obj({
    checkSuite: opt(nul(obj({ app: nul(obj({ name: str() })), workflowRun: nul(obj({ workflow: obj({ name: str() }) })) }))),
  }),
  StatusContext: obj({}),
});
const checkContexts = nodes(checkContext, { totalCount: opt(num()), pageInfo: opt(pageInfo) });
/** The latest commit and its checks: none where GitHub withheld the commit, or its checks. */
const commits = nodes(obj({ commit: obj({ oid: key, statusCheckRollup: nul(obj({ state: str('PENDING'), contexts: checkContexts })) }) }));

const viewedFiles = obj({ pageInfo, nodes: list(obj({ path: key, viewerViewedState: str('UNVIEWED') })) });

/** A label is nothing without its name; its colour and description are filled in by `readLabels`. */
const labels = obj({ totalCount: num(), nodes: list(obj({ name: key, color: str(), description: nul(str()) })) });

const pullRequest = obj({
  id: key,
  databaseId: num(),
  number: num(),
  url: str(),
  title: str(),
  body: str(),
  bodyHTML: str(),
  createdAt: str(),
  updatedAt: str(),
  author: actor,
  authorAssociation: str('NONE'),
  state: str('OPEN'),
  isDraft: flag(),
  merged: flag(),
  baseRefOid: key,
  headRefOid: key,
  baseRefName: str(),
  headRefName: str(),
  reviewDecision: nul(str()),
  mergeable: str('UNKNOWN'),
  mergeStateStatus: str('UNKNOWN'),
  viewerCanUpdate: flag(),
  additions: num(),
  deletions: num(),
  changedFiles: num(),
  baseRef: nul(obj({ branchProtectionRule: nul(obj({ requiredApprovingReviewCount: nul(num()) })) })),
  reviewRequests: nodes(obj({ requestedReviewer: reviewer })),
  labels,
  latestReviews: nodes(obj({ id: key, state: str(), submittedAt: nul(str()), author: actor })),
  pending: nodes(obj({ id: key, databaseId: num(), viewerDidAuthor: flag(), reviewCommit: opt(nul(obj({ oid: key }))), comments: count })),
  reviewThreads,
  commits,
  timelineItems,
  files: opt(nul(viewedFiles)),
});

const prAnswer = obj({
  viewer: obj({ login: str() }),
  repository: nul(
    obj({
      id: key,
      nameWithOwner: key,
      name: str(),
      // The branch a pull request not in a stack is based on (`PrStore.stackExpected`): unknown where GitHub withheld it.
      defaultBranchRef: opt(nul(obj({ name: key }))),
      owner: obj({ __typename: str('User'), login: key }),
      mergeCommitAllowed: flag(),
      squashMergeAllowed: flag(),
      rebaseMergeAllowed: flag(),
      isPrivate: opt(flag()),
      viewerPermission: nul(str()),
      pullRequest: nul(pullRequest),
    }),
  ),
});

const read = <T>(shape: Shape, value: unknown): T | null => {
  const v = decode(shape, value);
  return v === MISSING ? null : (v as T);
};
const within = (answer: unknown): unknown => (answer as { repository?: { pullRequest?: unknown } | null } | null)?.repository?.pullRequest;

/**
 * GitHub showed the pull request but left out something it cannot be
 * shown without (its ID, its head or base commit, its repository's name):
 * said as a failed load, with GitHub's reason when it gave one, not as
 * "not found".
 */
export class IncompleteAnswer extends Error {
  constructor() {
    super('GitHub’s answer for this pull request is missing parts it cannot be shown without. Try again; if it stays so, your access to the repository may be limited.');
    this.name = 'IncompleteAnswer';
  }
}

/** PR_QUERY's answer; `repository` or its `pullRequest` null when GitHub does not show them. Throws IncompleteAnswer. */
export function readPr(answer: unknown): PrQueryResult {
  const shown = within(answer) != null;
  const res = read<PrQueryResult>(prAnswer, answer);
  if (!res || (shown && !res.repository?.pullRequest)) throw new IncompleteAnswer();
  const pr = res.repository?.pullRequest;
  if (pr) pr.labels = readLabels(pr.labels);
  return res;
}

/** The colour of a label GitHub gave none, or one that is not a colour: its own default grey. */
const NO_COLOR = 'ededed';

/**
 * A pull request's `labels` field as GitHub may answer it (the dashboard's
 * rows and the pull request page both read it through here): no labels
 * when the field is missing or null, a label without a name left out, a
 * colour that is not six hex digits replaced by GitHub's default grey, a
 * description that is empty or not text null, and a count never below what is held.
 */
export function readLabels(value: unknown): Labels {
  const read = decode(labels, value) as Labels;
  const nodes = read.nodes.map((l) => {
    const color = /^#?([0-9a-f]{6})$/i.exec(l.color)?.[1].toLowerCase() ?? NO_COLOR;
    return { name: l.name, color, description: l.description || null };
  });
  return { totalCount: Math.max(read.totalCount, nodes.length), nodes };
}

/** A further page of review threads (PR_THREADS_PAGE); null when the answer has none (stop paging). */
export function readThreadsPage(answer: unknown): PullRequestData['reviewThreads'] | null {
  const page = (within(answer) as { reviewThreads?: unknown } | null | undefined)?.reviewThreads;
  return page == null ? null : read(reviewThreads, page);
}

/** A further page of the timeline (PR_TIMELINE_PAGE); null when the answer has none. */
export function readTimelinePage(answer: unknown): { pageInfo: BackwardPageInfo; nodes: TimelineItem[] } | null {
  const page = (within(answer) as { timelineItems?: unknown } | null | undefined)?.timelineItems;
  return page == null ? null : read(timelineItems, page);
}

/** A further page of one thread's comments (THREAD_COMMENTS_PAGE); null when the thread is gone. */
export function readCommentsPage(answer: unknown): { pageInfo: PageInfo; nodes: ThreadComment[] } | null {
  const page = (answer as { node?: { comments?: unknown } | null } | null)?.node?.comments;
  return page == null ? null : read(nodes(threadComment, { pageInfo }), page);
}

/** PR_CHECKS's answer: the head it is for and its latest commit's checks; null when GitHub does not show the pull request (or its head). */
export function readChecks(answer: unknown): { headRefOid: string; commits: PullRequestData['commits'] } | null {
  const pr = within(answer);
  return pr == null ? null : read(obj({ headRefOid: key, commits }), pr);
}

/** COMMIT_CHECKS's answer: the commit and its checks (none where GitHub withheld them); null when GitHub does not show the commit. */
export function readCommitChecks(answer: unknown): PullRequestData['commits']['nodes'][number]['commit'] | null {
  const commit = (answer as { repository?: { object?: unknown } | null } | null)?.repository?.object;
  return commit == null ? null : read(obj({ oid: key, statusCheckRollup: nul(obj({ state: str('PENDING'), contexts: checkContexts })) }), commit);
}

const deployment = obj({
  id: key,
  state: nul(str()),
  environment: nul(str()),
  createdAt: str(),
  commitOid: str(),
  latestStatus: nul(obj({ state: str(), environmentUrl: nul(str()), logUrl: nul(str()), createdAt: str() })),
});

/** PR_DEPLOYMENTS's answer: the commits GitHub named, each with the deployments it showed (none where it withheld them). */
export function readDeployments(answer: unknown): DeploymentCommit[] {
  const page = (within(answer) as { commits?: unknown } | null | undefined)?.commits;
  const found = read<{ nodes: { commit: DeploymentCommit }[] }>(nodes(obj({ commit: obj({ oid: key, deployments: nodes(deployment) }) })), page);
  return found?.nodes.map((n) => n.commit) ?? [];
}

/** A pull request as the stack read reads it (`stackRow` in api/queries.ts), with the stack fields `stack` names. */
const stackRow = (stack: Record<string, Shape>) =>
  obj({
    id: key,
    number: key,
    title: str(),
    isDraft: flag(),
    state: str('OPEN'),
    updatedAt: str(),
    reviewDecision: nul(str()),
    repository: obj({ nameWithOwner: key, name: str(), owner: obj({ login: str() }) }),
    baseRefName: str(),
    headRefName: str(),
    headRepository: nul(obj({ nameWithOwner: key })),
    commits: nodes(obj({ commit: obj({ statusCheckRollup: nul(obj({ state: str() })) }) })),
    ...stack,
  });
const position = obj({ position: num() });
/** A stack as a row names it (DASH_STACK): its members by position, a member GitHub does not show with no pull request. */
const namedStack = obj({ id: key, size: num(), entries: nodes(obj({ position: num(), pullRequest: nul(obj({ number: key })) })) });
/** The pull request's own stack (PR_STACK): each member GitHub shows as a row. */
const ownStack = obj({ id: key, size: num(), entries: nodes(obj({ position: num(), pullRequest: nul(stackRow({})) })) });
const stackAnswer = obj({
  pullRequest: nul(obj({ id: key, stack: opt(nul(ownStack)), stackEntry: opt(nul(position)) })),
  pullRequests: obj({ totalCount: num(), nodes: list(stackRow({ stack: opt(nul(namedStack)), stackEntry: opt(nul(position)) })) }),
});

/**
 * PR_STACK_QUERY's answer (with or without native stack fields): the pull
 * request's own stack and the repository's open pull requests, each row
 * whole or left out. `listed` is how many rows GitHub listed, those it
 * would not show among them, for telling whether the list was cut short.
 * Null when there is no repository in the answer.
 */
export function readStack(answer: unknown): { repository: NonNullable<PrStackResult['repository']>; listed: number } | null {
  const raw = (answer as { repository?: { pullRequests?: { nodes?: unknown } | null } | null } | null)?.repository;
  if (raw == null) return null;
  const repository = read<NonNullable<PrStackResult['repository']>>(stackAnswer, raw);
  const rows = raw.pullRequests?.nodes;
  return repository && { repository, listed: Array.isArray(rows) ? rows.length : 0 };
}
